import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

function ShareLinkDialog({ open, onClose, shareUrl, isGenerating, onGenerateLink }) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [expirationHours, setExpirationHours] = useState(168); // 7 days default
  const [copied, setCopied] = useState(false);

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      enqueueSnackbar(t('LinkCopiedToClipboard'), { variant: 'success' });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      enqueueSnackbar(t('FailedToCopyLink'), { variant: 'error' });
    }
  };

  const handleGenerateLink = () => {
    onGenerateLink(expirationHours);
    setCopied(false);
  };

  const handleClose = () => {
    setCopied(false);
    onClose();
  };

  const expirationOptions = [
    { value: 24, label: t('OneDayExpiration') },
    { value: 72, label: t('ThreeDaysExpiration') },
    { value: 168, label: t('OneWeekExpiration') }
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Box sx={{ p: 3 }}>
      <DialogTitle>
        <Typography variant="h4" component="div">
        {t('GenerateShareLink')}
        </Typography>
      </DialogTitle>
            <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={(theme) => ({
                position: 'absolute',
                right: 16,
                top: 16,
                color: theme.palette.grey[500],
            })}
            >
            <CancelIcon />
            </IconButton>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('ShareLinkDescription')}
          </Typography>

          <FormControl fullWidth>
            <InputLabel>{t('LinkExpiration')}</InputLabel>
            <Select
              value={expirationHours}
              label={t('LinkExpiration')}
              onChange={(e) => setExpirationHours(e.target.value)}
              disabled={isGenerating}
            >
              {expirationOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {!shareUrl && !isGenerating && (
            <Alert severity="info">
              {t('ClickGenerateLinkInfo')}
            </Alert>
          )}

          {isGenerating && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
              <CircularProgress size={24} />
              <Typography>{t('CreatingShortLink') || t('GeneratingShareLink')}</Typography>
            </Box>
          )}

          {shareUrl && !isGenerating && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('ShareLinkExpiresIn', { expiration: expirationOptions.find(opt => opt.value === expirationHours)?.label })}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  fullWidth
                  value={shareUrl}
                  variant="outlined"
                  size="small"
                  slotProps={{
                    input: {
                      readOnly: true,
                      style: { fontSize: '0.875rem' }
                    }
                  }}
                />
                <Tooltip title={copied ? t('Copied') : t('CopyToClipboard')}>
                  <IconButton 
                    onClick={handleCopyToClipboard}
                    color={copied ? "success" : "primary"}
                  >
                    {copied ? <CheckIcon /> : <ContentCopyIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>{t('Important')}:</strong> {t('ShareLinkWarning')}
                </Typography>
              </Alert>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {!shareUrl && (
          <Button 
            onClick={handleGenerateLink} 
            variant="contained" 
            disabled={isGenerating}
          >
            {t('GenerateLink')}
          </Button>
        )}
        {shareUrl && (
          <Button 
            onClick={handleGenerateLink} 
            variant="outlined"
            disabled={isGenerating}
          >
            {t('GenerateNewLink')}
          </Button>
        )}
      </DialogActions>
      </Box>
    </Dialog>
  );
}

export default ShareLinkDialog;